<template>
  <el-card shadow="never" v-loading="loading">
    <template slot="header">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ name: $root.getDashboardPath() }">Dashboard</el-breadcrumb-item>
        <el-breadcrumb-item :to="{ name: 'Availability' }">Global Availability</el-breadcrumb-item>
      </el-breadcrumb>
    </template>
    <el-tabs v-model="activeTab">
      <el-tab-pane name="units" label="Units">
        <el-card shadow="never">
          <div style="display: grid; grid-template-columns: 2fr 1fr; gap: 1rem;">
            <!-- Primary Facility Selection -->
              <el-card shadow="never" :body-style="{ display: 'flex', flexDirection: 'column', gap: '1rem'}">
              <div>
                <div style="margin-bottom: 1rem; display: flex; gap: 1rem; align-items: center;">
                  <i class="el-icon-office-building"></i>
                  <label>Select Facilities:</label>
                </div>
                <el-select
                  v-model="selectedFacilities"
                  multiple
                  filterable
                  placeholder="Choose one or more facilities"
                  style="width: 100%"
                >
                  <el-option
                    v-for="facility in facilities"
                    :key="facility.key"
                    :label="facility.label"
                    :value="facility.key"
                  >
                  </el-option>
                </el-select>
              </div>

              <div>
                <div style="margin-bottom: 1rem; display: flex; gap: 1rem; align-items: center;">
                  <i class="el-icon-location"></i>
                  <label>Quick Regional Presets:</label>
                </div>
                <el-button-group>
                  <el-button size="small" plain @click="() => selectFacilityPreset(losAngelesArea)">Los Angeles Area</el-button>
                  <el-button size="small" plain @click="() => selectFacilityPreset(orangeCounty)">Orange County</el-button>
                  <el-button size="small" plain @click="() => selectFacilityPreset(sanFranciscoBayArea)">San Francisco Bay Area</el-button>
                  <el-button size="small" plain @click="() => selectFacilityPreset(seattleArea)">Seattle Area</el-button>
                  <el-button size="small" plain @click="() => selectFacilityPreset(houstonArea)">Houston Area</el-button>
                  <el-button size="small" plain @click="() => selectFacilityPreset(northernNJArea)">Northern New Jersey</el-button>
                  <el-button size="small" plain @click="() => selectFacilityPreset(nYArea)">New York City</el-button>
                  <el-button size="small" plain @click="() => selectFacilityPreset(orlandoArea)">Orlando</el-button>
                </el-button-group>
              </div>
            </el-card>

            <!-- Search Filters & Actions -->
              <el-card shadow="never" :body-style="{ display: 'flex', flexDirection: 'column', gap: '1rem'}">
              <div style="display: flex; flex-direction: column; gap: 20px; flex: 1;">
                <div style="width: 100%;">
                  <label style="display: flex; gap: 1rem; align-items: center; margin-bottom: 1rem;">
                    <i class="el-icon-s-home" style="margin-right: 4px;"></i>
                    Unit Type:
                  </label>
                  <el-radio-group v-model="type" size="small" style="width: 100%; display: flex;">
                    <el-radio-button label="Warehouse"></el-radio-button>
                    <el-radio-button label="Office"></el-radio-button>
                    <el-radio-button label="Parking"></el-radio-button>
                  </el-radio-group>
                </div>
                <div>
                  <div style="margin-bottom: 0.5rem; display: flex; gap: 1rem; align-items: center; justify-content: space-between; flex-wrap: wrap;">
                    <div style="display: flex; gap: 1rem; align-items: center;">
                      <i class="el-icon-s-grid"></i>
                      <span>Size Range (sq ft):</span>
                    </div>
                    <el-select
                      v-model="selectedTags"
                      multiple
                      collapse-tags
                      clearable
                      filterable
                      placeholder="Tags"
                      size="small"
                      style="width: 45%; min-width: 160px;"
                    >
                      <el-option
                        v-for="tag in tagsBasedOnType"
                        :key="tag"
                        :label="tag"
                        :value="tag"
                      >
                      </el-option>
                    </el-select>
                  </div>
                  <div style="display: flex; gap: 8px;">
                    <el-tooltip
                      content="Min Size"
                      placement="top"
                    >
                      <el-input-number
                        v-model="minSize"
                        :step="100"
                        :disabled="type === 'Parking'"
                        placeholder="Min"
                        size="small"
                        style="width: 100%"
                      ></el-input-number>
                    </el-tooltip>
                    <span style="display: flex; align-items: center; padding: 0 4px;">-</span>
                    <el-tooltip
                      content="Max Size"
                      placement="top"
                    >
                      <el-input-number
                        v-model="maxSize"
                        :step="100"
                        :disabled="type === 'Parking'"
                        placeholder="Max"
                        size="small"
                        style="width: 100%"
                      ></el-input-number>
                    </el-tooltip>
                  </div>
                </div>

                <el-button
                  type="primary"
                  size="medium"
                  @click="getUnits"
                  style="width: 100%; margin-top: auto;"
                  :icon="'el-icon-search'"
                >
                  Search Units
                </el-button>
              </div>
            </el-card>
          </div>
        </el-card>
        <el-card shadow="never" v-loading="unitsLoading">
          <el-table :data="units" @sort-change="fetchWithSort">
            <el-table-column width="100" label="Facility" sortable prop="facilityData.code">
              <template slot-scope="scope">
                {{ scope.row.facilityData.code.toUpperCase() }}
              </template>
            </el-table-column>
            <el-table-column width="100" prop="name" label="Unit"></el-table-column>
            <el-table-column width="80" prop="computedSize" label="Size" sortable></el-table-column>
            <el-table-column width="170" label="Asking Price" prop="askingPrice">
              <template slot-scope="scope">
                <el-tag>
                  {{ scope.row.price | currencySign($root.state.facilityData) }}
                  <span v-if="scope.row.securityFeeEnabled">
                    + {{ scope.row.securityFee | currencySign($root.state.facilityData) }}
                  </span>
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column width="120" prop="type" label="Unit kind"></el-table-column>
            <el-table-column width="250" prop="tags" label="Tags">
              <template slot-scope="scope">
                <el-tag :key="tag._id" v-for="tag of scope.row.tags">{{ tag.value }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column width="150" prop="status" label="Status" :formatter="(row, column, cellValue) => $options.filters.printableStatus(cellValue)">
            </el-table-column>
            <el-table-column width="150" label="Move out">
              <template slot-scope="scope">
                <div v-if="scope.row.status === 'OVERDUE_MOVEOUT'">
                  Unknown
                </div>
                <div v-if="scope.row.availability && scope.row.availability !== '1/1/1970'">
                  {{ scope.row.availability }}
                </div>
                <div v-else> - </div>
              </template>
            </el-table-column>
            <el-table-column label="Actions">
              <template slot-scope="scope">
                <el-button @click="() => emailUnitPhotos(scope.row)" icon="el-icon-message" size="small" v-if="scope.row.images && scope.row.images.length > 0" circle>
                </el-button>
                <el-button @click="() => copyText(scope.row)" icon="el-icon-copy-document" size="small" circle>
                </el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-pagination
            v-show="!unitsLoading"
            hide-on-single-page
            background
            :total="totalCount"
            layout="prev, pager, next"
            @current-change="handlePages"
          >
          </el-pagination>
        </el-card>
      </el-tab-pane>
      <el-tab-pane name="customers" label="Customers">
        <el-card>
          <template slot="header">
            Search customer in all facilities by:
            <el-select :value="customerFilterParam" placeholder="Filter by" @change="setCustomerFilterParam">
              <el-option label="Company" value="company"></el-option>
              <el-option label="Full name" value="fullName"></el-option>
              <el-option label="e-mail" value="email"></el-option>
              <el-option label="Phone" value="phone"></el-option>
              <el-option label="Tax ID" value="taxId"></el-option>
            </el-select>
            <el-select
              :value="textSearchValue"
              filterable
              remote
              :remote-method="searchData"
              reserve-keyword
              style="width: 24rem"
              placeholder="Search customer"
              size="large"
              :loading="loadingSearchBar"
              no-match-text="No match found"
              no-data-text="No match found"
              loading-text="Searching..."
              @change="fetchCustomer"
            >
              <el-option
                v-for="item in result"
                :key="item._id"
                :label="`Customer: ${item.company}`"
                :value="item._id"
              >
              </el-option>
              <i slot="prefix" class="el-input__icon el-icon-search"></i>
            </el-select>
          </template>
          <div v-if="JSON.stringify(customer) !== '{}'">
        <el-row :gutter="10">
          <el-col :span="16">
            <el-descriptions title="Company">
              <el-descriptions-item label="Name">
                {{ customer.company }}
              </el-descriptions-item>
              <el-descriptions-item label="Tax ID">
                {{ customer.taxId }}
              </el-descriptions-item>
              <el-descriptions-item label="Industry/Business">
                {{ customer.business }}
              </el-descriptions-item>
              <el-descriptions-item label="Salesforce/Customer ID">
                {{ customer.salesforceId || '-' }}
              </el-descriptions-item>
              <el-descriptions-item label="Facilities">
                {{ facilityNames }}
              </el-descriptions-item>
            </el-descriptions>
            <el-divider> </el-divider>
            <el-descriptions title="Payments">
              <template slot="extra">
                <el-switch
                    v-model="customer.blockPayments"
                    active-text="Block"
                    inactive-text="Unblock"
                    active-color="#ff4949"
                    inactive-color="#13ce66"
                    disabled
                  >
                  </el-switch>
              </template>
              <el-descriptions-item label="ACH">
                <el-switch
                    v-model="customer.blockAchPayments"
                    active-text="Block"
                    inactive-text="Unblock"
                    active-color="#ff4949"
                    inactive-color="#13ce66"
                    disabled
                  >
                  </el-switch>
              </el-descriptions-item>
              <el-descriptions-item label="Cheque">
                <el-switch
                    v-model="customer.blockChequePayments"
                    active-text="Block"
                    inactive-text="Unblock"
                    active-color="#ff4949"
                    inactive-color="#13ce66"
                    disabled
                  >
                  </el-switch>
              </el-descriptions-item>
            </el-descriptions>
            <el-divider> </el-divider>
            <el-descriptions title="Customer Primary Contact" :column="3">
              <el-descriptions-item label="Email">
                {{ customer.email }}
              </el-descriptions-item>
              <el-descriptions-item label="Full Name">
                {{
                  customer.primaryContact
                    ? customer.primaryContact.name
                    : customer.fullName
                }}
              </el-descriptions-item>
              <el-descriptions-item label="Address Line 1">
                {{ customer.address }}
              </el-descriptions-item>
              <el-descriptions-item label="Address Line 2">
                {{ customer.address2 }}
              </el-descriptions-item>
              <el-descriptions-item label="City">
                {{ customer.city }}
              </el-descriptions-item>
              <el-descriptions-item label="State">
                {{ customer.state }}
              </el-descriptions-item>
              <el-descriptions-item label="Zip Code">
                {{ customer.zip }}
              </el-descriptions-item>
              <el-descriptions-item label="Phone Number">
                {{ customer.phone }}
              </el-descriptions-item>
            </el-descriptions>
            <el-divider> </el-divider>
            <el-descriptions title="Late fee" :column="3">
              <el-descriptions-item label="Chronic Late">
                 <el-switch
                    v-model="customer.chronicLate"
                    active-text="Enable"
                    inactive-text="Disable"
                    active-color="#ff4949"
                    inactive-color="#13ce66"
                    disabled
                  >
                  </el-switch>
              </el-descriptions-item>
            </el-descriptions>
            <el-divider> </el-divider>
            <el-descriptions title="Write Off Balance" :column="3" >
              <el-descriptions-item label="WriteOff" >
                 <el-switch
                    v-model="customer.skipReports"
                    active-text="True"
                    inactive-text="False"
                    active-color="#ff4949"
                    inactive-color="#13ce66"
                    disabled
                  >
                  </el-switch>
              </el-descriptions-item>
            </el-descriptions>
          </el-col>
          <el-col :span="8">
            <el-descriptions
              title="Insurance"
              direction="vertical"
              border
              :column="1"
            >
              <el-descriptions-item label="Document">
                <el-button v-if="customer.insurances.length >= 1">
                  <el-link
                    icon="el-icon-document"
                    :href="customer.insurances[0].url"
                  >
                    View Insurance
                  </el-link>
                </el-button>
                <div v-else>No Data</div>
                <el-button v-if="customer.insurances.length > 1">
                  <el-dropdown>
                    <span>
                      Versions
                   </span>
                   <i class="el-icon-caret-bottom el-icon-right"></i>
                   <template #dropdown>
                     <el-dropdown-menu>
                        <el-dropdown-item v-for="(item, index) in customer.insurances" :key="item._id">
                          <el-link v-if="index !== 0" :href="item.url">
                           {{ new Date(item.createdAt).toLocaleString() }}
                          </el-link>
                       </el-dropdown-item>
                     </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                </el-button>
              </el-descriptions-item>
              <el-descriptions-item
                v-if="customer.insurances.length"
                label="Insurance End"
              >
                {{
                  customer.insurances[0].end
                    ? customer.insurances[0].end
                    : ""
                | formatDate }}
              </el-descriptions-item>
            </el-descriptions>
            <el-divider></el-divider>
            <el-descriptions
              title="Identification"
              direction="vertical"
              border
              :column="1"
            >
              <el-descriptions-item v-if="customer.identification" label="Name">
                {{ customer.identification.name }}
              </el-descriptions-item>
            </el-descriptions>
            <el-image
              v-if="customer.identification && customer.identification.url"
              :src="customer.identification.url"
            >
            </el-image>
          </el-col>
        </el-row>
          </div>
        </el-card>
      </el-tab-pane>
    </el-tabs>

    <el-dialog :visible.sync="sendEmailDialogVisible" destroy-on-close>
      <h1>Email Photos</h1>
      <el-form :model="emailForm" ref="emailForm" v-loading="loading">
        <el-form-item label="Name"
          prop="name"
          :rules="[
            {
              required: true,
              message: 'Please input name',
              trigger: 'blur',
            }
          ]"
        >
          <el-input v-model="emailForm.name" />
        </el-form-item>
        <el-form-item label="Email"
          prop="email"
          :rules="[
           {
             required: true,
             message: 'Please input email address',
             trigger: 'blur',
           },
           {
             type: 'email',
             message: 'Please input correct email address',
             trigger: ['blur', 'change'],
           },
         ]"
       >
         <el-input v-model="emailForm.email" />
       </el-form-item>
       <el-form-item label="Send Floor Plan">
          <el-checkbox v-model="sendFloorPlan" :disabled="!Boolean(hasFloorPlan)"></el-checkbox>
        </el-form-item>
       <el-form-item>
          <el-button type="primary" :disabled="!emailForm.email" @click="sendEmail">
            Send
          </el-button>
        </el-form-item>
      </el-form>
    </el-dialog>
  </el-card>
</template>

<script>
import RsGlobalAvailabilityApi from '../api/globalAvailability';
import axios from 'axios';
import { getLocations } from '../utils/commonApi';
import { getCustomerById } from '../api/customers';
import { computeUnitPricing, round } from '../utils/pricing';
import { CUSTOMER_API_BASE, EMAIL_API_BASE } from '../env';
import { escapeSpecialCharacters } from '../utils/regExp';
import { unitEmailV2Template } from '../utils/emailTemplates';
import unitTags, { officeTags, parkingTags } from '../utils/unitTags';

export default {
  name: 'GlobalAvailability',
  data() {
    return {
      emailFacility: null,
      unit: null,
      sendEmailDialogVisible: false,
      emailForm: {
        email: '',
        name: '',
      },
      sendFloorPlan: false,
      sorting: false,
      sortBy: '',
      abortController: null,
      activeTab: 'units',
      customerFilterParam: 'company',
      textSearchValue: '',
      result: [],
      loading: true,
      loadingSearchBar: false,
      unitsLoading: false,
      facilities: [],
      facilitiesData: [],
      facilityNames: [],
      facilitiesReponse: [],
      selectedFacilities: [],
      units: [],
      pages: 0,
      customer: {},
      totalCount: 0,
      currentPage: 0,
      titles: ['Facilities', 'Applied Facilities'],
      minSize: null,
      maxSize: null,
      selectedTags: [],
      type: 'Warehouse',
      losAngelesArea: ['Northridge', 'Los Angeles - Downtown', 'Los Angeles - Gardena', 'South Gate'],
      orangeCounty: ['Anaheim', 'Santa Ana'],
      sanFranciscoBayArea: ['Richmond', 'Hayward', 'San Jose - Alma', 'Santa Clara', 'South San Francisco - Spruce', 'South San Francisco - Harris'],
      seattleArea: ['Renton', 'Southcenter', 'Redmond'],
      houstonArea: ['Houston - East of Downtown', 'Houston - The Heights'],
      northernNJArea: ['Kearny', 'Saddle Brook', 'North Bergen'],
      nYArea: ['Queens - Ridgewood', 'Queens - Jamaica', 'Mount Vernon'],
      orlandoArea: ['Orlando - La Quinta', 'Orlando - South'],
      // losAngelesArea: ['NRG', 'LAD', 'LAG', 'SOG'],
      // orangeCounty: ['ANA', 'SNA'],
      // sanFranciscoBayArea: ['RMC', 'HAY', 'SJC', 'SCL', 'SFS', 'SFH'],
      // seattleArea: ['RTN', 'SCR', 'RED'],
      // houstonArea: ['HED', 'HTH'],
      // northernNJArea: ['KEO', 'SBK', 'NBR'],
      // nYArea: ['QNR', 'QNJ', 'MVR'],
    };
  },
  async mounted() {
    this.abortController = new AbortController();
    this.facilitiesReponse = await getLocations(this.$keycloak.token);
    this.facilities = this.facilitiesReponse.map(fac => ({ key: fac._id, label: fac.title }));
    // await this.getUnits();
    this.loading = false;
  },
  computed: {
    price() {
      if (this.unit && this.emailFacility) {
        return computeUnitPricing(this.unit, this.emailFacility);
      }
      return null;
    },
    tagsBasedOnType() {
      if (this.type === 'Office') {
        return officeTags;
      }

      if (this.type === 'Parking') {
        return parkingTags;
      }

      return unitTags;
    },
    hasFloorPlan() {
      const facilityData = this.emailFacility;
      const officialDocs = facilityData ? facilityData.officialDocs : [];
      return [...officialDocs].find(doc => doc.name === 'Floor Plan');
    },
    facilitySecurityFee() {
      const facilityData = this.emailFacility;
      if (facilityData && facilityData.securityFee && facilityData.securityFee.enabled && facilityData.securityFee.fee > 0) {
        const computedSecurityFee = facilityData.securityFee.fee * this.unit.computedSize / 100;
        const label = facilityData.securityFee.label || 'Security';

        return {
          fee: computedSecurityFee,
          label,
        };
      }

      return null;
    },
  },
  watch: {
    type() {
      this.selectedTags = [];
    },
  },
  methods: {
    async copyText(unit) {
      try {
        const availability = unit.availability ? unit.availability.split('/') : '';
        const tagsExist = unit.tags.filter(item => item.value !== 'No Photos').length > 0;
        const tagsText = tagsExist ? unit.tags.filter(item => item.value !== 'No Photos').map(tag => tag.value).join(', ') : '';
        const availabilityText = availability.length > 1 ? ` (Available ${availability[0]}/${availability[1]})` : '';
        const availabilityAndTags = `${availabilityText}${tagsText ? ' - ' : ''}${tagsText}`;
        const label = unit.securityFeeEnabled && unit.facilityData.securityFee ? unit.facilityData.securityFee.label : 'Security Fee';
        const securityFeeText = unit.securityFeeEnabled ? ` + $${unit.securityFee.toLocaleString('en-US')}/mo ${label}` : '';
        const price = Math.round(unit.price).toLocaleString('en-US');
        const unitType = unit.type === 'Office' ? 'Office' : 'Unit';
        const text = `${unitType} ${unit.name} - ${unit.computedSize} SF - $${price}/mo${securityFeeText}${availabilityAndTags}`;
        await navigator.clipboard.writeText(text);
        this.$message.success({
          title: 'Copied',
          message: 'Unit info copied to clipboard',
        });
      } catch (err) {
        this.$message.error({
          title: 'Error',
          message: err.message,
        });
      }
    },
    async sendEmail() {
      this.loading = true;
      const facilityData = this.emailFacility;
      try {
        const tags = this.unit.tags.map(item => item.value).filter(tag => tag !== 'No Photos');
        if (this.unit.images.length > 0) {
          const mjmlTemplate = unitEmailV2Template(
            this.emailForm.name,
            facilityData.title,
            facilityData.addressInfo,
            this.unit,
            { ...facilityData.manager, ...this.$keycloak.tokenParsed },
            tags,
            this.price,
            facilityData.pricing.currency,
            this.facilitySecurityFee,
            facilityData.rentTaxFlag,
            facilityData.tax ? facilityData.tax.rentTaxLabel : null,
          );
          await axios.post(`${EMAIL_API_BASE}/send/email`, {
            from: this.$keycloak.tokenParsed.email,
            to: this.emailForm.email,
            cc: this.$keycloak.tokenParsed.email,
            subject: 'Requested ReadySpaces Unit Photos',
            facility: facilityData._id,
            mjml: true,
            body: mjmlTemplate,
            attachments: [...this.unit.images, ...(this.hasFloorPlan && this.sendFloorPlan ? [{
              name: 'FloorPlan.pdf',
              type: 'application/pdf',
              url: this.hasFloorPlan.url,
            }] : [])],
          }, {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          });

          this.emailForm.email = '';
          this.emailForm.name = '';
          this.$message.success({
            title: 'Email sent',
            message: 'Email sent successfully',
          });
          this.loading = false;
        } else {
          this.loading = false;
          this.$message.warning({
            title: 'Images Does not exist',
            message: 'This unit does not have any images',
          });
        }
      } catch (err) {
        this.loading = false;
        this.$message.error({
          title: 'Error',
          message: 'Send email failed',
        });
      }
    },
    emailUnitPhotos(unit) {
      this.emailFacility = this.facilitiesReponse.find(fac => fac._id === unit.facility);
      this.sendEmailDialogVisible = true;
      this.unit = unit;
    },
    computeSecurityFee(row) {
      let fee = 0;

      const securityFeeEnabled = row.facilityData && row.facilityData.securityFee && row.facilityData.securityFee.enabled && row.facilityData.securityFee.fee > 0;
      if (securityFeeEnabled) {
        fee = round((row.facilityData.securityFee.fee / 100) * row.computedSize);
      }
      return fee;
    },
    async fetchCustomer(customerId) {
      this.loading = true;
      this.customer = await getCustomerById(customerId, null, this.$keycloak.token);
      this.facilityNames = this.facilitiesReponse
        .filter(facilityData => this.customer.locations.indexOf(facilityData._id) !== -1)
        .map(facilityData => facilityData.title)
        .join(', ');

      this.loading = false;
    },
    setCustomerFilterParam(value) {
      this.customerFilterParam = value;
    },
    routeToCustomer(customerId) {
      if (customerId.startsWith('unit')) {
        const unitId = customerId.split('unit').pop();
        const [unit] = this.units.filter(item => item._id === unitId);
        if (!this.facility) {
          this.facility = unit.facility;
        }

        this.$router.push({ name: 'Facility Unit', params: { facility: unit.facility, unit: unitId } });
        this.result = [];
        this.units = [];
        return;
      }
      if (this.$router.name !== 'Customer' || this.$router.name !== 'Facility Customer') {
        this.$router.push({ name: this.facility ? 'Facility Customer' : 'Customer', params: { id: customerId, ...(this.facility ? { facility: this.facility } : {}) } });
        this.result = [];
        this.units = [];
        return;
      }
      this.$router.push({ name: this.facility ? 'Facility Customer' : 'Customer', params: { id: customerId, ...(this.facility ? { facility: this.facility } : {}) } });
      this.result = [];
      this.units = [];
    },
    async fetchWithSort({ column, prop, order }) {
      if (prop) {
        this.sortBy = prop;
        this.sorting = order === 'descending' ? -1 : order === 'ascending' ? 1 : 0;
      }

      await this.getUnits();
    },
    async searchData(searchText) {
      if (!searchText) return;

      if (this.loading) {
        this.abortController.abort();
        this.abortController = new AbortController();
      }

      try {
        this.loadingSearchBar = true;
        const searchValue = escapeSpecialCharacters(searchText);
        if (!searchValue) return;

        if (this.customerFilterParam === 'taxId' || this.customerFilterParam === 'phone') {
          const params = { [this.customerFilterParam]: searchValue };
          const customers = await axios.get(`${CUSTOMER_API_BASE}/customers/v1/search`, {
            signal: this.abortController.signal,
            params,
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          });
          this.result = customers.data;
          this.loadingSearchBar = false;
          return;
        }

        const customers = await axios.get(`${CUSTOMER_API_BASE}/customers/v1/get/all`, {
          signal: this.abortController.signal,
          params: { fullname: searchValue, email: true },
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        this.result = customers.data;

        this.loadingSearchBar = false;
      } catch (err) {
        this.loadingSearchBar = false;
      }
    },
    selectFacilityPreset(preset) {
      // console.log(this.facilities.filter( facility => preset.includes(facilityData.title)).map(facility => facilityData._id));
      const filterLocations = [];
      preset.forEach((locationCode) => {
        const [foundFilter] = this.facilities.filter(facilityData => locationCode === facilityData.label);

        if (foundFilter) {
          filterLocations.push(foundFilter.key);
        }
      });

      this.selectedFacilities = filterLocations;
    },
    async handlePages(newPage) {
      this.currentPage = newPage;
      await this.getUnits();
    },
    async getUnits() {
      this.unitsLoading = true;
      try {
        const rsUnitsApi = new RsGlobalAvailabilityApi(this.$keycloak.token);
        const disableSizeFilters = this.type === 'Parking';

        if (!this.selectedFacilities.length) {
          this.$message.warning({
            message: 'Please select facilities',
          });

          return;
        }
        const units = await rsUnitsApi.getGlobalAvailability({
          ...(this.selectedFacilities.length ? { facilities: this.selectedFacilities.toString() } : {}),
          ...(this.selectedTags.length ? { tags: this.selectedTags.toString() } : {}),
          ...(!disableSizeFilters && this.minSize ? { minSize: this.minSize } : {}),
          ...(this.sorting ? { sortBy: this.sortBy } : {}),
          ...(this.sorting ? { sorting: this.sorting } : {}),
          ...(!disableSizeFilters && this.maxSize ? { maxSize: this.maxSize } : {}),
          ...(this.currentPage ? { pageNumber: this.currentPage } : {}),
          type: this.type,
        });

        this.units = units.data.map((unit) => {
          unit.price = computeUnitPricing(unit, unit.facilityData);

          unit.securityFeeEnabled = unit.facilityData.securityFee && unit.facilityData.securityFee.enabled && unit.facilityData.securityFee.fee > 0;
          unit.securityFee = this.computeSecurityFee(unit);
          unit.askingPrice = unit.price + unit.securityFee;

          return unit;
        });
        this.totalCount = units.pagination.totalCount;
        this.pages = this.totalCount / 10;
        this.currentPage = units.pagination.pageNo;
      } catch (err) {
        this.$message.error({
          message: err.response ? err.response.data : err.message,
        });
        this.units = [];
        this.totalCount = 0;
        this.currentPage = 0;
      }
      this.unitsLoading = false;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/GlobalAvailability.vue